/**
 * Reglas del formulario de usuarios (`/user/create`) y traducción de los errores
 * que devuelve `users/UpsertUserStandar`.
 *
 * Los límites NO son inventados: salen de las DataAnnotations de `UserRequestDto`
 * en ApiCore (el DTO base de `UpdStarStandUserDto`, que es el body del endpoint) y
 * están verificados contra dev.api.tealca.com. Si el front deja escribir de más,
 * el API responde 400 con el detalle en inglés y el usuario sólo veía "Error:"
 * en el modal, sin texto.
 */

/** ProfileID de "Super Administrador" en MiTealca (ApplicationID = 2). */
export const SUPER_ADMIN_PROFILE_ID = 201

/** Mensaje de login duplicado. Compartido para que el campo Usuario se desbloquee. */
export const LOGIN_TAKEN_MESSAGE = 'El usuario ya existe'

/**
 * Con qué perfil precargar el combo cuando se abre un usuario que ya existe.
 *
 * Hay dos fuentes y no coinciden: el listado (`GetUserAccount` /
 * `DynamicUserAccount`) y el perfil de MiTealca (`GET /Users/{id}?aplicationCode=2`).
 * El listado puede devolver el perfil corporativo de la persona —"Ti" (61) para
 * enderson.namias, "Especialista De Bases De Datos" (125) para tatiana.paez—
 * cuando en MiTealca las dos son Super Administrador. Por eso el 201 siempre
 * gana: es el único caso que hay que dejar preseleccionado y bloqueado.
 */
export const pickProfileToPreload = (
  profileInApp?: number,
  profileFromList?: number,
): number | undefined =>
  profileInApp === SUPER_ADMIN_PROFILE_ID
    ? profileInApp
    : (profileFromList ?? profileInApp)

export const USER_FIELD_LIMITS = {
  /** API: [StringLength(30, MinimumLength = 2)] */
  userName: { min: 2, max: 30 },
  /** API: [StringLength(30, MinimumLength = 2)] */
  userLastName: { min: 2, max: 30 },
  /** API: [StringLength(10, MinimumLength = 1)] */
  userIdentificationNumber: { min: 1, max: 10 },
  /** API: [StringLength(50, MinimumLength = 2)]. El front ya exigía 3. */
  userLogin: { min: 3, max: 50 },
  /** API: [StringLength(30, MinimumLength = 7)], medido YA con el prefijo. */
  userPhone: { min: 7, max: 30 },
  /** El API no valida el correo (ni formato ni largo): este tope es del front. */
  userEmail: { min: 5, max: 100 },
} as const

/**
 * Dígitos que acepta el campo Teléfono según la operadora.
 *
 * Un móvil lleva SÓLO los 7 finales: la operadora (412, 414, …) se concatena al
 * guardar. Un fijo va sin prefijo, así que carga el código de área dentro del
 * propio número, y en BD conviven las dos formas: 10 dígitos (`2122578022`) y
 * 11 con el 0 inicial (`02122578022`). Medido sobre los 218 usuarios de dev:
 * 134 fijos de 10 y 9 de 11+. Exigir 10 exactos dejaba a esos 9 sin poder
 * guardar NINGÚN cambio, ni siquiera el correo.
 */
export const PHONE_DIGITS = {
  FIJO: { min: 7, max: 10 },
  MOVIL: { min: 7, max: 7 },
} as const

export const phoneDigitsFor = (
  codePhoneNumber?: string,
): { min: number; max: number } =>
  codePhoneNumber && codePhoneNumber !== 'FIJO'
    ? PHONE_DIGITS.MOVIL
    : PHONE_DIGITS.FIJO

/**
 * Login sugerido a partir del nombre y el apellido.
 *
 * Reglas del negocio: con documento venezolano es `nombre.apellido`; con
 * cualquier otro es `nombreapellido.VE`. En ambos casos se usa sólo la primera
 * palabra de cada campo.
 *
 * La versión anterior recortaba a 20 + 20 caracteres a ciegas en cuanto la suma
 * pasaba de 40, lo que mutilaba un apellido corto por culpa de un nombre largo
 * ("Mariadelourdes Paz" -> "Mariadelourdes.Paz" quedaba bien, pero
 * "Mariadelourdesdelvalle Perez" perdía letras del apellido sin razón). Acá se
 * recorta siempre la parte más larga, y sólo lo necesario para entrar en el
 * máximo que acepta el API.
 */
export const buildUserLogin = (
  userName: string,
  userLastName: string,
  /** true cuando el documento NO es venezolano: el login lleva sufijo `.VE`. */
  foreignDocument: boolean,
): string => {
  const firstWord = (value: string) =>
    (value ?? '').trim().split(/\s+/)[0] ?? ''

  let name = firstWord(userName)
  let lastName = firstWord(userLastName)
  if (name === '' && lastName === '') return ''

  const suffix = foreignDocument ? '.VE' : ''
  const separator = foreignDocument ? '' : '.'
  const budget =
    USER_FIELD_LIMITS.userLogin.max - suffix.length - separator.length

  while (name.length + lastName.length > budget) {
    if (name.length >= lastName.length) name = name.slice(0, -1)
    else lastName = lastName.slice(0, -1)
  }

  return `${name}${separator}${lastName}${suffix}`
}

/** Etiqueta visible de cada campo, para poder armar el resumen del modal. */
const FIELD_LABEL: Record<string, string> = {
  userLogin: 'Usuario',
  userName: 'Nombre',
  userLastName: 'Apellido',
  userIdentificationNumber: 'Nro. Documento',
  userIdentificationTypeID: 'Tipo de documento',
  userPhone: 'Teléfono',
  userEmail: 'Correo',
  profileID: 'Perfiles',
}

/** Campo del DTO del API (en minúsculas) → campo del formulario. */
const API_FIELD_TO_FORM: Record<string, string> = {
  username: 'userName',
  userlastname: 'userLastName',
  useridentificationnumber: 'userIdentificationNumber',
  taxidentificationtypeid: 'userIdentificationTypeID',
  userlogin: 'userLogin',
  useremail: 'userEmail',
  userphone: 'userPhone',
  // La clave la genera el front; si el API se queja de ella el admin no tiene
  // dónde corregirla, así que se reporta junto al usuario.
  userpassword: 'userLogin',
  profileslist: 'profileID',
}

/** Traducción al español del límite de cada campo, para no mostrar el texto en inglés. */
const LIMIT_MESSAGE: Record<string, string> = {
  userName: `El nombre debe tener entre ${USER_FIELD_LIMITS.userName.min} y ${USER_FIELD_LIMITS.userName.max} caracteres`,
  userLastName: `El apellido debe tener entre ${USER_FIELD_LIMITS.userLastName.min} y ${USER_FIELD_LIMITS.userLastName.max} caracteres`,
  userIdentificationNumber: `El documento debe tener entre ${USER_FIELD_LIMITS.userIdentificationNumber.min} y ${USER_FIELD_LIMITS.userIdentificationNumber.max} caracteres`,
  userLogin: `El usuario debe tener entre ${USER_FIELD_LIMITS.userLogin.min} y ${USER_FIELD_LIMITS.userLogin.max} caracteres`,
  userPhone: `El teléfono debe tener entre ${USER_FIELD_LIMITS.userPhone.min} y ${USER_FIELD_LIMITS.userPhone.max} dígitos, contando la operadora`,
  userEmail: 'Este campo es requerido',
  userIdentificationTypeID: 'Este campo es requerido',
  profileID: 'Debe seleccionar un perfil válido',
}

/**
 * Mensajes de negocio del SP `security.UpsertUserStandarApi`. Llegan con HTTP 200
 * y `didError: true`, ya en español, pero sin decir a qué campo pertenecen.
 */
const BUSINESS_MESSAGES: { match: RegExp; field: string; message: string }[] = [
  {
    match: /usuario ya existe/i,
    field: 'userLogin',
    message: LOGIN_TAKEN_MESSAGE,
  },
  {
    match: /correo ya existe/i,
    field: 'userEmail',
    message: 'Este correo ya está registrado en otro usuario',
  },
  {
    match: /identificador ya existe/i,
    field: 'userIdentificationNumber',
    message: 'Ya existe un usuario con este número de documento',
  },
]

export interface UserApiError {
  /** Texto para el modal, con el detalle por campo si lo hay. */
  message: string
  /** Errores listos para pintar sobre cada input del formulario. */
  fieldErrors: Record<string, string>
}

const summarize = (
  header: string,
  fieldErrors: Record<string, string>,
): string => {
  const detail = Object.entries(fieldErrors).map(
    ([field, message]) => `${FIELD_LABEL[field] ?? field}: ${message}`,
  )
  return detail.length > 0 ? [header, ...detail].join('\n') : header
}

/**
 * Normaliza cualquiera de las tres formas en que falla el endpoint:
 *
 *  1. HTTP 400 ProblemDetails de ASP.NET —
 *     `{ errors: { "[0].UserName": ["The field UserName must be a string…"] } }`,
 *     que es lo que sale cuando se rompe una DataAnnotation. El cuerpo NO es un
 *     arreglo, y leerlo como `data[0].errorMessage` reventaba dejando el modal
 *     en "Error:" sin texto: de ahí que el largo del nombre no se explicara nunca.
 *  2. HTTP 200 con `[{ didError: true, errorMessage }]` y un mensaje del SP.
 *  3. Cualquier otra cosa (500, red caída): se usa el fallback.
 */
export const mapUserApiError = (data: any, fallback: string): UserApiError => {
  const fieldErrors: Record<string, string> = {}

  // 1. ProblemDetails de validación del modelo.
  if (!!data?.errors && typeof data.errors === 'object') {
    const unmapped: string[] = []

    Object.entries(data.errors as Record<string, string[]>).forEach(
      ([key, messages]) => {
        // Las claves vienen como "[0].UserName" porque el body es una lista.
        const apiField = key.split('.').pop()?.toLowerCase() ?? ''
        const formField = API_FIELD_TO_FORM[apiField]
        if (!formField) {
          // Parámetros que no son campos del formulario (p. ej. AccountSiteID
          // en el listado). Sin esto el modal mostraba sólo el encabezado y
          // ningún detalle, que es el mismo agujero que este mapeo vino a tapar.
          unmapped.push(
            `${key.split('.').pop()}: ${messages?.[0] ?? ''}`.trim(),
          )
          return
        }
        fieldErrors[formField] =
          LIMIT_MESSAGE[formField] ?? messages?.[0] ?? fallback
      },
    )

    if (Object.keys(fieldErrors).length === 0) {
      return {
        message: unmapped.length > 0 ? unmapped.join('\n') : fallback,
        fieldErrors,
      }
    }

    return {
      message: summarize('Revise los datos del usuario:', fieldErrors),
      fieldErrors,
    }
  }

  // 2. Mensaje de negocio del SP.
  const raw = Array.isArray(data)
    ? data[0]?.errorMessage
    : typeof data?.errorMessage === 'string'
      ? data.errorMessage
      : undefined

  if (typeof raw === 'string' && raw.trim() !== '') {
    const known = BUSINESS_MESSAGES.find((b) => b.match.test(raw))
    if (known) {
      fieldErrors[known.field] = known.message
      return {
        message: summarize('No se pudo guardar:', fieldErrors),
        fieldErrors,
      }
    }
    return { message: raw, fieldErrors }
  }

  // 3. Sin cuerpo aprovechable.
  return { message: fallback, fieldErrors }
}
